import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { pageTransition } from './utils/animations';
import { ThemeProvider, useTheme, getThemeColors } from './contexts/ThemeContext';

// All components loaded immediately for DOM availability
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import LazyVideo from './components/LazyVideo';
import About from './components/About';
import Chairman from './components/Chairman';
import Capabilities from './components/Capabilities';
import Products from './components/Products';
import ProcessFlow from './components/ProcessFlow';
import Quality from './components/Quality';
import Sectors from './components/Sectors';
import Testimonials from './components/Testimonials';
import CTABanner from './components/CTABanner';
import Contact from './components/Contact';
import Footer from './components/Footer';

// Performance monitoring utilities
import { performanceMonitor } from './utils/lazyLoader';

const AppContent = () => {
  const { isDarkMode } = useTheme();
  const colors = getThemeColors(isDarkMode);

  useEffect(() => {
    // Performance monitoring
    performanceMonitor.mark('app-start');

    // Initialize scroll-based animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animated');
        }
      });
    }, observerOptions);

    // Observe all elements with animate-on-scroll class
    const elementsToAnimate = document.querySelectorAll('.animate-on-scroll');
    elementsToAnimate.forEach((el) => observer.observe(el));

    // Mark content as loaded since all components are now immediately available
    performanceMonitor.mark('content-loaded');

    // Cleanup
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className="App">
      <AnimatePresence mode="wait">
        <motion.div
          variants={pageTransition}
          initial="initial"
          animate="animate"
          exit="exit"
          className={`min-h-screen ${colors.bg.primary} ${colors.text.primary} overflow-x-hidden transition-colors duration-300`}
        >
          {/* Deferred video background - loads after page is interactive */}
          <LazyVideo
            src={`${process.env.PUBLIC_URL}/backgrond.mp4`}
            autoPlay={true}
            muted={true}
            loop={true}
            playsInline={true}
            loadDelay={2000}
            onCanPlay={() => {
              performanceMonitor.mark('video-loaded');
            }}
          />

          {/* Navigation */}
          <Navbar />

          {/* Main Content */}
          <main id="main-content">
            <Hero />
            {/* Grouped sections */}
            <div
              id="grouped-sections"
              className="relative"
            >
              {/* Background overlay for readability (light/dark handled in CSS) */}
              <div className="absolute inset-0 static-sections-overlay pointer-events-none" style={{ zIndex: 2 }} aria-hidden="true" />
              <div className="relative" style={{ zIndex: 10 }}>
                <About />
                <Chairman />
                <Capabilities />
                <Products />
                <ProcessFlow />
                <Quality />
                <Sectors />
                <Testimonials />
                <CTABanner />
                <Contact />
              </div>
            </div>
          </main>

          {/* Footer */}
          <Footer />
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
